import React from 'react';
import { useHistory } from 'react-router-dom';
import ClassificationDropdown from './ClassificationDropdown';
import PartnerSearchCell from './PartnerSearchCell';
import InvoiceDatePicker from '../ui/InvoiceDatePicker';
import { CURRENCY_OPTIONS } from '../../utils/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

// Single DOCUMENT routes, never the lists -- these open for a trade-ops
// operator as well as for finance.
//
// **2026-09-09:** three of these were singular (`/invoicing/purchase-order`,
// `/credit-note`, `/debit-note`) and no such route exists in App.js, so the
// "view linked document" arrow on those three line types opened a blank
// screen. Found while sweeping every link that sends a user to an /invoicing/
// address; same family as the redirect bug, one character each.
const SOURCE_TYPE_ROUTES = {
  OUTGOING_INVOICE: '/invoicing/outgoing',
  PROFORMA_INVOICE: '/invoicing/proforma',
  PURCHASE_ORDER: '/invoicing/purchase-orders',
  INCOMING_INVOICE: '/invoicing/incoming',
  CREDIT_NOTE: '/invoicing/credit-notes',
  DEBIT_NOTE: '/invoicing/debit-notes',
};

const NA_AMOUNT_PER_UNIT_TYPES = ['PROFORMA_INVOICE', 'CREDIT_NOTE', 'PURCHASE_ORDER', 'DEBIT_NOTE'];

const FinanceRow = ({
  line,
  classifications,
  onChange,
  onRemove,
  isSubtotalRow,
  showClassify,
  fxLoading,
  quantity,
}) => {
  const history = useHistory();
  const fmt = (val) => {
    if (val == null || val === '' || val === 0) return '';
    const n = parseFloat(val);
    if (isNaN(n)) return '';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtRate = (val) => {
    if (val == null || val === '') return '-';
    const n = parseFloat(val);
    if (isNaN(n)) return '-';
    return n.toFixed(4);
  };

  if (isSubtotalRow) {
    return (
      <tr className="fc-subtotal-row">
        <td className="fc-type-cell">Sub Total:</td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td className="fc-amount-cell">{fmt(line.invoice_amount)}</td>
        <td className="fc-amount-cell fc-usd-cell">{fmt(line.converted_amount_usd)}</td>
        <td></td>
        <td className="fc-amount-cell">{fmt(line.amount_paid)}</td>
        <td className="fc-amount-cell">{fmt(line.amount_open)}</td>
        <td className="fc-amount-cell">{fmt(line.budget_amount)}</td>
        <td className="fc-amount-cell fc-variance-cell">{fmt(line.budget_variance)}</td>
        <td></td>
        <td></td>
        <td></td>
      </tr>
    );
  }

  const handleField = (field, value) => {
    onChange(line.id, { [field]: value });
  };

  const isSynced = line.is_synced === true;
  const isSystemRow = isSynced || line.is_system_line !== false;

  return (
    <tr className={`fc-data-row${isSynced ? ' fc-synced-row' : ''}`}>
      {/* Type */}
      <td className="fc-type-cell">
        {isSystemRow ? (
          <>
            <span className="fc-line-label">{line.line_label || line.line_type_label}</span>
            {isSynced && (
              <span className="fc-synced-badge" title="Auto-synced from invoice">
                <i className="fas fa-link"></i>
              </span>
            )}
            {line.source_invoice_id && line.source_invoice_type && (
              <span
                className="fc-source-link"
                onClick={() => {
                  const base = SOURCE_TYPE_ROUTES[line.source_invoice_type];
                  if (base) history.push(`${base}/${line.source_invoice_id}`);
                }}
                title="View linked document"
              >
                <i className="fas fa-external-link-alt"></i>
              </span>
            )}
          </>
        ) : (
          <input
            type="text"
            value={line.line_label || ''}
            onChange={(e) => handleField('line_label', e.target.value)}
            className="fc-input fc-label-input"
            placeholder="Row name"
          />
        )}
      </td>

      {/* Classify */}
      <td>
        {showClassify ? (
          <ClassificationDropdown
            value={line.classification}
            onChange={(val) => handleField('classification', val)}
            classifications={classifications}
          />
        ) : null}
      </td>

      {/* Partner (CRM search) */}
      <td>
        {isSynced ? (
          <span className="fc-calculated fc-synced-value">{line.partner || ''}</span>
        ) : (
          <PartnerSearchCell
            value={line.partner || ''}
            crmId={line.partner_crm_id}
            onChange={(partnerId, partnerName) => {
              onChange(line.id, { partner: partnerName, partner_crm_id: partnerId });
            }}
          />
        )}
      </td>

      {/* Description */}
      <td>
        <textarea
          value={line.description || ''}
          onChange={(e) => handleField('description', e.target.value)}
          className="fc-input fc-description-textarea"
          placeholder="Description"
          rows={1}
        />
      </td>

      {/* Invoice Date */}
      <td>
        {isSynced ? (
          <span className="fc-calculated fc-synced-value">{line.invoice_date || ''}</span>
        ) : (
          <InvoiceDatePicker
            name="invoice_date"
            value={line.invoice_date || ''}
            onChange={(e) => handleField('invoice_date', e.target.value)}
            className="fc-date-picker"
          />
        )}
      </td>

      {/* Invoice Currency */}
      <td>
        {isSynced ? (
          <span className="fc-calculated fc-synced-value">{line.invoice_currency || 'USD'}</span>
        ) : (
          <select
            value={line.invoice_currency || 'USD'}
            onChange={(e) => handleField('invoice_currency', e.target.value)}
            className="fc-input fc-currency-select"
            disabled={fxLoading}
          >
            {CURRENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.value}</option>
            ))}
          </select>
        )}
      </td>

      {/* Amount per unit */}
      <td className="fc-amount-cell">
        {NA_AMOUNT_PER_UNIT_TYPES.includes(line.line_type) ? (
          <span className="fc-calculated fc-na">N/A</span>
        ) : (
          <span className="fc-calculated">
            {(() => {
              const q = parseFloat(quantity) || 0;
              const inv = parseFloat(line.invoice_amount) || 0;
              if (q === 0 || inv === 0) return '';
              return (inv / q).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
            })()}
          </span>
        )}
      </td>

      {/* Invoice Amount */}
      <td className="fc-amount-cell">
        {isSynced ? (
          <span className="fc-calculated">{fmt(line.invoice_amount)}</span>
        ) : (
          <input
            type="text"
            inputMode="decimal"
            value={line.invoice_amount ?? ''}
            onChange={(e) => handleField('invoice_amount', e.target.value)}
            className="fc-input fc-amount-input"
            placeholder="0.00"
          />
        )}
      </td>

      {/* Amount USD */}
      <td className="fc-amount-cell fc-usd-cell">
        <span className="fc-calculated">
          {fxLoading ? 'Converting...' : fmt(line.converted_amount_usd)}
        </span>
      </td>

      {/* FX Rate */}
      <td className="fc-amount-cell">
        <span className="fc-calculated">{fxLoading ? '...' : fmtRate(line.fx_rate_used)}</span>
      </td>

      {/* Amount Paid / Received */}
      <td className="fc-amount-cell">
        {isSynced ? (
          <span className="fc-calculated">{fmt(line.amount_paid)}</span>
        ) : (
          <input
            type="text"
            inputMode="decimal"
            value={line.amount_paid ?? ''}
            onChange={(e) => handleField('amount_paid', e.target.value)}
            className="fc-input fc-amount-input"
            placeholder="0.00"
          />
        )}
      </td>

      {/* Amount Open */}
      <td className="fc-amount-cell">
        <span className="fc-calculated">{fmt(line.amount_open)}</span>
      </td>

      {/* Budget Amount */}
      <td className="fc-amount-cell">
        <span className="fc-calculated">{fmt(line.budget_amount)}</span>
      </td>

      {/* Budget Variance */}
      <td className="fc-amount-cell fc-variance-cell">
        <span className={`fc-calculated ${parseFloat(line.budget_variance || 0) < 0 ? 'fc-negative' : ''}`}>
          {fmt(line.budget_variance)}
        </span>
      </td>

      {/* Due Date */}
      <td>
        {isSynced ? (
          <span className="fc-calculated fc-synced-value">{line.due_date || ''}</span>
        ) : (
          <InvoiceDatePicker
            name="due_date"
            value={line.due_date || ''}
            onChange={(e) => handleField('due_date', e.target.value)}
            className="fc-date-picker"
          />
        )}
      </td>

      {/* Payment Date */}
      <td>
        {isSynced ? (
          <span className="fc-calculated fc-synced-value">{line.paid_date || ''}</span>
        ) : (
          <InvoiceDatePicker
            name="paid_date"
            value={line.paid_date || ''}
            onChange={(e) => handleField('paid_date', e.target.value)}
            className="fc-date-picker"
          />
        )}
      </td>

      {/* Remove */}
      <td className="fc-actions-cell">
        {!isSystemRow && (
          <button className="fc-remove-btn" onClick={() => onRemove(line.id)} title="Remove row" type="button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </td>
    </tr>
  );
};

export default FinanceRow;
